/**
 * Job-related business logic utilities
 */

import {
  buildMonthOptionsBetweenDates,
  formatShortMonthDayYear,
  getDateInLocalTZFromDateStr,
  getTodayInPT,
  today,
} from "../datetime";
import { getUserDisplayName } from "./user";

/**
 * Return the project title shown in project UI surfaces.
 *
 * @param {Object} project - Project or job API row.
 * @returns {string}
 */
export function getProjectTitle(project) {
  return project?.description || project?.display_name || "Untitled Project";
}

/**
 * Return the customer label shown for a project.
 *
 * @param {Object} project - Project or job API row.
 * @returns {string}
 */
export function getProjectCustomerLabel(project) {
  return project?.customer?.name || "No customer";
}

/**
 * Return the default address label for a project.
 *
 * @param {Object} project - Project or job API row.
 * @returns {string}
 */
export function getProjectAddressLabel(project) {
  return project?.default_address?.address || "No default address";
}

/**
 * Return a compact project date range.
 *
 * @param {string|null} startDate - Project start date as YYYY-MM-DD.
 * @param {string|null} endDate - Project end date as YYYY-MM-DD.
 * @returns {string}
 */
export function getProjectDateRange(startDate, endDate) {
  if (!startDate && !endDate) {
    return "Dates not set";
  }
  if (!startDate) {
    return `Until ${formatShortMonthDayYear(getDateInLocalTZFromDateStr(endDate))}`;
  }
  if (!endDate) {
    return `${formatShortMonthDayYear(getDateInLocalTZFromDateStr(startDate))} onward`;
  }
  return `${formatShortMonthDayYear(getDateInLocalTZFromDateStr(startDate))} - ${formatShortMonthDayYear(getDateInLocalTZFromDateStr(endDate))}`;
}

/**
 * Build month dropdown options spanning a project's date_start–date_end.
 * Missing end defaults to today; missing start yields no options.
 *
 * @param {Object|null|undefined} project - Project / job API row.
 * @returns {Array<{value: string, label: string}>}
 */
export function buildProjectMonthOptions(project) {
  const startRaw = project?.date_start;
  if (!startRaw) {
    return [];
  }

  const startDate = getDateInLocalTZFromDateStr(
    String(startRaw).split("T")[0]
  );
  const endRaw = project?.date_end;
  const endDate = endRaw
    ? getDateInLocalTZFromDateStr(String(endRaw).split("T")[0])
    : today();

  return buildMonthOptionsBetweenDates(startDate, endDate);
}

/**
 * Return all assigned manager names for compact project display.
 *
 * @param {Array<Object>} managers - Serialized project managers.
 * @returns {string}
 */
export function getProjectManagerNames(managers) {
  if (!managers?.length) {
    return "No manager assigned";
  }
  const names = managers
    .map((manager) => getUserDisplayName(manager, ""))
    .filter(Boolean);
  return names.length ? names.join(", ") : "No manager assigned";
}

/**
 * Return a display label for project date-window active status.
 *
 * @param {boolean} isActive - Whether today falls in ``date_start``–``date_end``.
 * @returns {string}
 */
export function getJobActiveStatusLabel(isActive) {
  return isActive ? "Active" : "Inactive";
}

/**
 * Associates scopes with their respective jobs.
 *
 * @param {Array} jobs - The list of jobs.
 * @param {Array} scopes - The list of scopes.
 * @param {string|Date} [date] - Optional date to filter scopes. Only scopes matching this date will be included.
 * @returns {Array} An array of tuples where each tuple contains a job and an array of associated scopes.
 *
 * @example
 * // Get all scopes for all jobs
 * const jobsWithScopes = scopesByJob(jobs, scopes);
 *
 * // Get only scopes for a specific date
 * const jobsWithScopesForDate = scopesByJob(jobs, scopes, '2025-07-25');
 * jobsWithScopesForDate.forEach(([job, jobScopes]) => {
 *   console.log(`Job ${job.id} has ${jobScopes.length} scopes for the specified date`);
 * });
 */
export function scopesByJob(jobs, scopes, date = null) {
  if (!jobs || !scopes) {
    return [];
  }

  // Filter scopes by date if date parameter is provided
  const filteredScopes = date
    ? scopes.filter(
        (scope) =>
          scope.date === date ||
          (scope.date instanceof Date &&
            date instanceof Date &&
            scope.date.getTime() === date.getTime()) ||
          (typeof scope.date === "string" &&
            typeof date === "string" &&
            scope.date === date) ||
          (scope.date instanceof Date &&
            typeof date === "string" &&
            scope.date.toISOString().split("T")[0] === date) ||
          (typeof scope.date === "string" &&
            date instanceof Date &&
            scope.date === date.toISOString().split("T")[0])
      )
    : scopes;

  const scopesPerJob = Map.groupBy(filteredScopes, (scope) => scope.job.id);
  // Use job.id to associate scopes and jobs
  return jobs.map((job) => [job, scopesPerJob.get(job.id) ?? []]);
}

/**
 * Gets all workers at a given job, on a given date, from the list of worker_assignments
 * in the scopes.
 *
 * @param {Array} jobs - The list of jobs.
 * @param {Array} scopes - The list of scopes.
 * @param {string|Date} [date] - Optional date to filter scopes. Only scopes matching this date will be included.
 *
 * @returns {Array} An array of worker objects associated with the job on the specified date.
 */
export function getWorkersByJob(jobs, scopes, date = null) {
  if (!jobs || !scopes) {
    return [];
  }

  // Filter scopes by date if date parameter is provided
  const filteredScopes = date
    ? scopes.filter(
        (scope) =>
          scope.date === date ||
          (scope.date instanceof Date &&
            date instanceof Date &&
            scope.date.getTime() === date.getTime()) ||
          (typeof scope.date === "string" &&
            typeof date === "string" &&
            scope.date === date) ||
          (scope.date instanceof Date &&
            typeof date === "string" &&
            scope.date.toISOString().split("T")[0] === date) ||
          (typeof scope.date === "string" &&
            date instanceof Date &&
            scope.date === date.toISOString().split("T")[0])
      )
    : scopes;

  const workersPerJob = Map.groupBy(filteredScopes, (scope) => scope.job.id);

  return jobs.map((job) => {
    const jobScopes = workersPerJob.get(job.id) || [];
    const workerAssignments = jobScopes.flatMap(
      (scope) => scope.worker_assignment || []
    );
    const uniqueWorkers = workerAssignments.filter(
      (worker, index, self) =>
        index === self.findIndex((w) => w.id === worker.id)
    );
    return { ...job, workers: uniqueWorkers };
  });
}

/**
 * Filters jobs by their end date, keeping only jobs that are active or ended within a specified date range.
 *
 * @param {Array} jobs - The list of jobs to filter.
 * @param {Object} [options] - Optional configuration for date filtering.
 * @param {Date|string|null} [options.startDate] - The start date of the range. Defaults to 1 year ago from today.
 * @param {Date|string|null} [options.endDate] - The end date of the range. Defaults to today.
 * @returns {Array} Filtered array of jobs that have no date_end or have date_end within the specified range.
 *
 * @example
 * // Filter jobs to show only active jobs or jobs that ended within the last year (default)
 * const activeJobs = filterJobsByDateRange(allJobs);
 *
 * // Filter jobs with custom date range
 * const recentJobs = filterJobsByDateRange(allJobs, {
 *   startDate: '2024-01-01',
 *   endDate: '2024-12-31'
 * });
 *
 * // Filter jobs that ended in the last 6 months
 * const sixMonthsAgo = new Date();
 * sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
 * const recentEndedJobs = filterJobsByDateRange(allJobs, {
 *   startDate: sixMonthsAgo
 * });
 */
export function filterJobsByDateRange(jobs, options = {}) {
  if (!jobs) return [];

  // Get default dates using centralized datetime utility
  const today = new Date(getTodayInPT());
  const defaultStartDate = new Date(today);
  defaultStartDate.setFullYear(defaultStartDate.getFullYear() - 1);

  // Parse options
  const { startDate = defaultStartDate } = options;

  // Convert dates to Date objects if needed
  const rangeStart =
    startDate instanceof Date ? startDate : new Date(startDate);

  // Filter jobs
  return jobs.filter((job) => {
    // If no date_end, job is active - always include
    if (!job.date_end) return true;

    // Check if job's end date falls within the specified range
    const jobEndDate = new Date(job.date_end);
    return jobEndDate > rangeStart;
  });
}
